/**
 * WhatsAppChannelSetup — COM-01
 *
 * Tenant Admin configures and activates the WhatsApp channel.
 * Guarded by admin.communication.whatsapp.manage.
 *
 * Secrets are stored as env:// references — NEVER as plaintext.
 * The backend (_public_view) strips all ref URIs and returns only
 * *_configured: bool flags; the frontend never sees the actual ref string.
 *
 * Lifecycle:  UNCONFIGURED → CONFIGURE → VALIDATE → ACTIVE
 */
import { useState, useEffect, useCallback } from "react";
import {
  MessageSquare, CheckCircle2, XCircle, AlertCircle, Loader2,
  ChevronDown, ChevronUp, ShieldCheck, RefreshCw, Zap,
} from "lucide-react";
import { whatsappChannelApi, hasCapability } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

// ── Status display metadata ───────────────────────────────────────────────────
const STATUS_META = {
  UNCONFIGURED: { label: "Unconfigured", color: "#706E6B", bg: "#F3F3F3", border: "#E5E5E5" },
  CONFIGURE:    { label: "Configured",   color: "#B05C14", bg: "#FFF3E0", border: "#E8A963" },
  VALIDATE:     { label: "Validated",    color: "#0176D3", bg: "#EEF4FF", border: "#0176D3" },
  ACTIVE:       { label: "Active",       color: "#027A3A", bg: "#EEF9F2", border: "#1B9948" },
};

const LIFECYCLE_STEPS = [
  { id: "CONFIGURE", label: "Configure" },
  { id: "VALIDATE",  label: "Validate"  },
  { id: "ACTIVE",    label: "Activate"  },
];

function stepState(stepId, status) {
  const order = ["CONFIGURE", "VALIDATE", "ACTIVE"];
  const cur = order.indexOf(status);
  const idx = order.indexOf(stepId);
  if (cur < 0) return "pending";
  if (idx < cur) return "done";
  if (idx === cur) return "active";
  return "pending";
}

// ── Secret reference input ────────────────────────────────────────────────────
// The user types the env var name (e.g. WA_ACCESS_TOKEN_T001).
// The component adds the env:// prefix before sending to the API.
function SecretField({ label, fieldKey, isConfigured, value, onChange, placeholder }) {
  const [replacing, setReplacing] = useState(false);
  const showInput = !isConfigured || replacing;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <label className="text-xs font-mono font-semibold text-[#444444]">{label}</label>
        {isConfigured && !replacing && (
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1 text-[10px] font-mono text-[#027A3A]">
              <CheckCircle2 className="w-3 h-3" /> Configured
            </span>
            <button type="button" onClick={() => setReplacing(true)}
              className="text-[10px] font-mono text-[#0176D3] hover:underline">
              Replace
            </button>
          </div>
        )}
        {isConfigured && replacing && (
          <button type="button" onClick={() => { setReplacing(false); onChange(""); }}
            className="text-[10px] font-mono text-[#706E6B] hover:underline">
            Cancel
          </button>
        )}
      </div>

      {showInput ? (
        <div className="flex items-center border border-[#E5E5E5] rounded-lg overflow-hidden bg-white focus-within:border-[#0176D3] transition-colors">
          <span className="px-2.5 py-2 text-xs font-mono bg-[#F3F3F3] text-[#706E6B] border-r border-[#E5E5E5] select-none flex-shrink-0">
            env://
          </span>
          <input
            type="text"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            data-testid={`secret-${fieldKey}`}
            className="flex-1 px-2.5 py-2 text-xs font-mono text-[#181818] bg-transparent outline-none"
          />
        </div>
      ) : (
        <div className="px-3 py-2 border border-[#E5E5E5] rounded-lg bg-[#F3F3F3]">
          <span className="text-xs font-mono text-[#706E6B]">env://••••••••••••••••</span>
        </div>
      )}
    </div>
  );
}

// ── Validation checklist ──────────────────────────────────────────────────────
function ChecklistPanel({ checks }) {
  return (
    <div className="space-y-1.5">
      {checks.map((c, i) => (
        <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg border text-xs"
          style={{
            background:  c.passed ? "#EEF9F2" : "#FEF2F2",
            borderColor: c.passed ? "#1B9948" : "#E03C31",
          }}>
          {c.passed
            ? <CheckCircle2 className="w-3.5 h-3.5 text-[#027A3A] flex-shrink-0 mt-0.5" />
            : <XCircle      className="w-3.5 h-3.5 text-[#E03C31] flex-shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0">
            <p className="font-mono font-semibold"
              style={{ color: c.passed ? "#027A3A" : "#E03C31" }}>{c.name}</p>
            {!c.passed && c.detail && (
              <p className="font-mono text-[#706E6B] mt-0.5">{c.detail}</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Collapsible section wrapper ───────────────────────────────────────────────
function Section({ title, badge, open, onToggle, children, testId }) {
  return (
    <div className="border border-[#E5E5E5] rounded-xl overflow-hidden bg-white">
      <button type="button" data-testid={testId}
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 bg-[#F3F3F3] hover:bg-[#EAEAEA] transition-colors text-left">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold font-sans text-[#181818]">{title}</span>
          {badge}
        </div>
        {open
          ? <ChevronUp   className="w-3.5 h-3.5 text-[#706E6B]" />
          : <ChevronDown className="w-3.5 h-3.5 text-[#706E6B]" />}
      </button>
      {open && <div className="p-4">{children}</div>}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
export default function WhatsAppChannelSetup() {
  const { user } = useAuth();
  const canManage = hasCapability(user, "admin.communication.whatsapp.manage");

  const [config,      setConfig]      = useState(null);
  const [status,      setStatus]      = useState("UNCONFIGURED");
  const [configVersion, setConfigVersion] = useState(0); // optimistic concurrency
  const [fetching,    setFetching]    = useState(true);
  const [loadErr,     setLoadErr]     = useState(false);

  // Non-secret form fields
  const [form, setForm] = useState({
    display_name:        "",
    phone_number_id:     "",
    business_account_id: "",
    business_number:     "",
    graph_version:       "v21.0",
  });

  // Secret env var names (without env:// prefix — UI only)
  const [secrets, setSecrets] = useState({ access_token: "", webhook_verify: "", app_secret: "" });

  // Action loading
  const [saving,       setSaving]       = useState(false);
  const [validating,   setValidating]   = useState(false);
  const [activating,   setActivating]   = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // Panel open state
  const [detailsOpen,  setDetailsOpen]  = useState(true);
  const [secretsOpen,  setSecretsOpen]  = useState(false);
  const [checksOpen,   setChecksOpen]   = useState(false);
  const [checks,       setChecks]       = useState(null);

  // ── Load ─────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setFetching(true);
    try {
      const r = await whatsappChannelApi.getConfig();
      setStatus(r.data.status || "UNCONFIGURED");
      const cfg = r.data.config;
      setConfig(cfg);
      if (cfg) {
        setConfigVersion(cfg.version ?? 0);
        setForm({
          display_name:        cfg.display_name        || "",
          phone_number_id:     cfg.phone_number_id     || "",
          business_account_id: cfg.business_account_id || "",
          business_number:     cfg.business_number     || "",
          graph_version:       cfg.graph_version       || "v21.0",
        });
      }
      setLoadErr(false);
    } catch {
      setLoadErr(true);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Save ──────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, expected_version: configVersion };
      if (secrets.access_token.trim())
        payload.access_token_secret_ref   = `env://${secrets.access_token.trim()}`;
      if (secrets.webhook_verify.trim())
        payload.webhook_verify_secret_ref = `env://${secrets.webhook_verify.trim()}`;
      if (secrets.app_secret.trim())
        payload.app_secret_secret_ref     = `env://${secrets.app_secret.trim()}`;

      const r = await whatsappChannelApi.saveConfig(payload);
      setStatus(r.data.status || "CONFIGURE");
      setConfig(r.data.config);
      setConfigVersion(r.data.config?.version ?? configVersion + 1);
      setSecrets({ access_token: "", webhook_verify: "", app_secret: "" });
      setChecks(null);
      toast.success("Configuration saved");
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Another admin modified this configuration. Reloading — please review and retry.");
        await load();
        return;
      }
      toast.error(e?.response?.data?.detail || "Failed to save configuration");
    } finally {
      setSaving(false);
    }
  };

  // ── Validate ──────────────────────────────────────────────────────────────
  const handleValidate = async () => {
    setValidating(true);
    setChecks(null);
    setChecksOpen(true);
    try {
      const r = await whatsappChannelApi.validate();
      setChecks(r.data.checks || []);
      if (r.data.valid) {
        setStatus("VALIDATE");
        toast.success("Validation passed — ready to activate");
        await load();
      } else {
        const n = (r.data.checks || []).filter(c => !c.passed).length;
        toast.error(`${n} check(s) failed — see results below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation error");
    } finally {
      setValidating(false);
    }
  };

  // ── Activate ──────────────────────────────────────────────────────────────
  const handleActivate = async () => {
    setActivating(true);
    try {
      const r = await whatsappChannelApi.activate();
      if (r.data.activated) {
        setStatus("ACTIVE");
        toast.success("WhatsApp channel is now ACTIVE");
        await load();
      } else {
        toast.error("Activation blocked: " + (r.data.blockers || []).join("; "));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    } finally {
      setActivating(false);
    }
  };

  // ── Deactivate ────────────────────────────────────────────────────────────
  const handleDeactivate = async () => {
    if (!window.confirm(
      "Deactivate the WhatsApp channel? Outbound messages will fall back to Mock (UAT) or fail explicitly (Production)."
    )) return;
    setDeactivating(true);
    try {
      await whatsappChannelApi.deactivate();
      setStatus("VALIDATE");   // deactivate preserves configuration → VALIDATE, not CONFIGURE
      setChecks(null);
      toast.success("Channel deactivated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Deactivation failed");
    } finally {
      setDeactivating(false);
    }
  };

  // ── Capability gate ───────────────────────────────────────────────────────
  if (!canManage) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-3"
        data-testid="whatsapp-setup-no-access">
        <ShieldCheck className="w-10 h-10 text-[#E5E5E5]" />
        <p className="text-sm text-[#706E6B] font-mono text-center max-w-sm">
          You need the{" "}
          <span className="font-semibold text-[#181818]">admin.communication.whatsapp.manage</span>{" "}
          permission to configure the WhatsApp channel.
        </p>
      </div>
    );
  }

  if (fetching) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-5 h-5 animate-spin text-[#0176D3]" />
    </div>
  );

  if (loadErr) return (
    <div className="flex flex-col items-center gap-3 py-16">
      <AlertCircle className="w-8 h-8 text-[#E03C31]" />
      <p className="text-sm text-[#706E6B]">Could not load channel configuration.</p>
      <button onClick={load}
        className="text-xs text-[#0176D3] hover:underline flex items-center gap-1">
        <RefreshCw className="w-3 h-3" /> Retry
      </button>
    </div>
  );

  const sm       = STATUS_META[status] || STATUS_META.UNCONFIGURED;
  const isActive = status === "ACTIVE";

  return (
    <div className="space-y-5" data-testid="whatsapp-channel-setup">

      {/* Header ─────────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between p-4 bg-white border border-[#E5E5E5] rounded-xl gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#25D36618] border border-[#25D366]/30
                          flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-5 h-5 text-[#25D366]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#181818]">WhatsApp Business Channel</p>
            <p className="text-xs text-[#706E6B] font-mono">
              Meta Cloud API · {form.phone_number_id ? `ID: ${form.phone_number_id}` : "Not configured"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span data-testid="whatsapp-channel-status"
            className="text-[10px] font-mono font-semibold px-2.5 py-1 rounded-full border"
            style={{ color: sm.color, background: sm.bg, borderColor: sm.border }}>
            {sm.label}
          </span>
          <button onClick={load}
            className="p-1.5 rounded-lg border border-[#E5E5E5] text-[#706E6B] hover:text-[#0176D3]
                       hover:border-[#0176D3]/40 transition-all">
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Provider badge ─────────────────────────────────────────────────────── */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs font-mono ${
        isActive
          ? "bg-[#EEF9F2] border-[#1B9948]/30 text-[#027A3A]"
          : "bg-[#FFF3E0] border-[#E8A963]/30 text-[#B05C14]"
      }`}>
        {isActive
          ? <><CheckCircle2 className="w-3.5 h-3.5" /> Configuration active · Meta delivery requires COM-02 live provider</>
          : <><AlertCircle  className="w-3.5 h-3.5" /> Provider: MockWhatsAppProvider (simulated — channel not active)</>
        }
      </div>

      {/* Lifecycle stepper ──────────────────────────────────────────────────── */}
      <div className="flex items-center bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl p-3 gap-0">
        {LIFECYCLE_STEPS.map((step, i) => {
          const state = stepState(step.id, status === "UNCONFIGURED" ? "" : status);
          return (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex items-center gap-2 flex-1">
                <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-colors"
                  style={{
                    background: state === "done" ? "#027A3A" : state === "active" ? "#0176D3" : "#E5E5E5",
                  }}>
                  {state === "done"
                    ? <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                    : <span className="text-[9px] font-mono font-bold"
                        style={{ color: state === "active" ? "white" : "#706E6B" }}>{i + 1}</span>
                  }
                </div>
                <span className="text-xs font-mono"
                  style={{
                    color:      state === "done" ? "#027A3A" : state === "active" ? "#0176D3" : "#706E6B",
                    fontWeight: state === "active" ? 700 : 500,
                  }}>
                  {step.label}
                </span>
              </div>
              {i < LIFECYCLE_STEPS.length - 1 && (
                <div className="w-6 h-px mx-2 flex-shrink-0 transition-colors"
                  style={{ background: state === "done" ? "#027A3A" : "#E5E5E5" }} />
              )}
            </div>
          );
        })}
      </div>

      {/* Channel Details section ─────────────────────────────────────────────── */}
      <Section title="Channel Details" testId="config-section-toggle"
        open={detailsOpen} onToggle={() => setDetailsOpen(v => !v)}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { key: "display_name",        label: "Channel Name",           ph: "e.g. Prashasta Business" },
              { key: "phone_number_id",      label: "Phone Number ID",        ph: "Meta Phone Number ID (numeric)" },
              { key: "business_account_id",  label: "Business Account ID",    ph: "Meta WABA ID" },
              { key: "business_number",      label: "Display Number (E.164)", ph: "+91XXXXXXXXXX" },
            ].map(({ key, label, ph }) => (
              <div key={key} className="space-y-1.5">
                <label className="text-xs font-mono font-semibold text-[#444444]">{label}</label>
                <input type="text" value={form[key]} disabled={isActive}
                  onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                  placeholder={ph}
                  data-testid={`config-${key}`}
                  className="w-full px-3 py-2 text-xs font-mono border border-[#E5E5E5] rounded-lg bg-white
                             focus:border-[#0176D3] focus:outline-none transition-colors
                             disabled:bg-[#F3F3F3] disabled:text-[#706E6B]"
                />
              </div>
            ))}
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-mono font-semibold text-[#444444]">Graph API Version</label>
            <input type="text" value={form.graph_version} disabled={isActive}
              onChange={e => setForm(f => ({ ...f, graph_version: e.target.value }))}
              data-testid="config-graph-version"
              className="w-28 px-3 py-2 text-xs font-mono border border-[#E5E5E5] rounded-lg bg-white
                         focus:border-[#0176D3] focus:outline-none transition-colors
                         disabled:bg-[#F3F3F3] disabled:text-[#706E6B]"
            />
          </div>
        </div>
      </Section>

      {/* Secret References section ───────────────────────────────────────────── */}
      <Section
        title="Secret References"
        testId="secrets-section-toggle"
        badge={
          <span className="text-[10px] font-mono text-[#706E6B] bg-white border border-[#E5E5E5] px-1.5 py-0.5 rounded">
            env:// only
          </span>
        }
        open={secretsOpen} onToggle={() => setSecretsOpen(v => !v)}>
        <div className="space-y-4">
          <p className="text-[10px] font-mono text-[#706E6B] leading-relaxed">
            Secrets are stored as <code className="bg-[#F3F3F3] px-1 rounded">env://VAR_NAME</code> references — never as plaintext.
            Enter the environment variable name; the <code className="bg-[#F3F3F3] px-1 rounded">env://</code> prefix is added automatically.
          </p>
          <SecretField label="Access Token" fieldKey="access_token"
            isConfigured={config?.access_token_configured || false}
            value={secrets.access_token}
            onChange={v => setSecrets(s => ({ ...s, access_token: v }))}
            placeholder="WA_ACCESS_TOKEN_T001" />
          <SecretField label="Webhook Verify Token" fieldKey="webhook_verify"
            isConfigured={config?.webhook_verify_configured || false}
            value={secrets.webhook_verify}
            onChange={v => setSecrets(s => ({ ...s, webhook_verify: v }))}
            placeholder="WA_WEBHOOK_VERIFY_T001" />
          <SecretField label="App Secret (optional)" fieldKey="app_secret"
            isConfigured={config?.app_secret_configured || false}
            value={secrets.app_secret}
            onChange={v => setSecrets(s => ({ ...s, app_secret: v }))}
            placeholder="WA_APP_SECRET_T001" />
        </div>
      </Section>

      {/* Validation checklist ────────────────────────────────────────────────── */}
      {checks && (
        <Section
          title="Validation Results"
          testId="validation-checklist-toggle"
          badge={
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border"
              style={checks.every(c => c.passed)
                ? { color: "#027A3A", background: "#EEF9F2", borderColor: "#1B9948" }
                : { color: "#E03C31", background: "#FEF2F2", borderColor: "#E03C31" }}>
              {checks.filter(c => c.passed).length}/{checks.length} passed
            </span>
          }
          open={checksOpen} onToggle={() => setChecksOpen(v => !v)}>
          <ChecklistPanel checks={checks} />
        </Section>
      )}

      {/* Action buttons ──────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap pt-1">
        {!isActive && (
          <button type="button" data-testid="whatsapp-save-btn"
            onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0176D3] text-white
                       text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
            {saving
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</>
              : "Save Configuration"}
          </button>
        )}

        {!isActive && status !== "UNCONFIGURED" && (
          <button type="button" data-testid="whatsapp-validate-btn"
            onClick={handleValidate} disabled={validating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#0176D3]
                       text-[#0176D3] text-xs font-semibold hover:bg-[#EEF4FF]
                       disabled:opacity-50 transition-colors">
            {validating
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Validating…</>
              : <><ShieldCheck className="w-3.5 h-3.5" /> Run Validation</>}
          </button>
        )}

        {status === "VALIDATE" && (
          <button type="button" data-testid="whatsapp-activate-btn"
            onClick={handleActivate} disabled={activating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#027A3A] text-white
                       text-xs font-semibold hover:opacity-90 disabled:opacity-50 transition-opacity">
            {activating
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Activating…</>
              : <><Zap className="w-3.5 h-3.5" /> Activate Channel</>}
          </button>
        )}

        {isActive && (
          <button type="button" data-testid="whatsapp-deactivate-btn"
            onClick={handleDeactivate} disabled={deactivating}
            className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[#E03C31]
                       text-[#E03C31] text-xs font-semibold hover:bg-[#FEF2F2]
                       disabled:opacity-50 transition-colors">
            {deactivating
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Deactivating…</>
              : "Deactivate Channel"}
          </button>
        )}
      </div>

      {/* Security note ───────────────────────────────────────────────────────── */}
      <div className="flex items-start gap-2.5 px-3.5 py-3 bg-[#FFFBE6] border border-[#E8A963] rounded-xl">
        <AlertCircle className="w-3.5 h-3.5 text-[#B05C14] flex-shrink-0 mt-0.5" />
        <p className="text-[10px] font-mono text-[#706E6B] leading-relaxed">
          Secrets are resolved from environment variables at runtime — never stored as plaintext.
          In production, ensure X-Forwarded-For trust is restricted to the trusted ingress/proxy path.
          Webhook endpoint: <code className="bg-white px-1 rounded">POST /api/whatsapp/webhook</code>
        </p>
      </div>
    </div>
  );
}
